import { useMemo, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, fmtDate, Empty } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, EnvelopeSimple, Phone, NotePencil, Sparkle, PaperPlaneTilt, GoogleLogo, ArrowsClockwise, Trash, PaperPlaneRight, Copy } from "@phosphor-icons/react";

const empty = { kind: "email", subject: "", body: "", client_id: "", project_id: "", interpreter_id: "", direction: "outbound", send_now: false, to: "" };

export default function Communications() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");
  const [drafting, setDrafting] = useState(false);
  const [viewing, setViewing] = useState(null);  // comm being viewed in detail sheet
  const [searchParams, setSearchParams] = useSearchParams();

  const resend = useMutation({
    mutationFn: async (commId) => (await api.post(`/communications/${commId}/resend`)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["comms"] });
      toast.success(`Resent to ${d.to}`);
      setViewing(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Resend failed"),
  });

  // Show a toast for the Gmail OAuth outcome once, then clean the URL
  useEffect(() => {
    const status = searchParams.get("gmail");
    if (!status) return;
    const reason = searchParams.get("reason") || "";
    const messages = {
      connected: { fn: toast.success, text: "Gmail connected — click Sync inbox to pull recent messages" },
      google_error: { fn: toast.error, text: `Google denied the request${reason ? ` (${reason})` : ""}. Common fix: in Google Cloud → OAuth consent screen, add your Gmail as a Test user, and make sure Gmail API is enabled.` },
      token_exchange_failed: { fn: toast.error, text: `Token exchange failed${reason ? ` (${reason})` : ""}. Try clicking Connect Gmail again — this can happen if the sign-in tab was left open too long.` },
      error_missing_state: { fn: toast.error, text: "The OAuth callback was missing state or code. Please try again." },
      error_unknown_state: { fn: toast.error, text: "This sign-in link has already been used or expired. Please try again." },
    };
    const m = messages[status];
    if (m) m.fn(m.text);
    searchParams.delete("gmail");
    searchParams.delete("reason");
    setSearchParams(searchParams, { replace: true });
    refetchGmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: comms = [], isLoading } = useQuery({ queryKey: ["comms"], queryFn: async () => (await api.get("/communications")).data });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const { data: interpreters = [] } = useQuery({ queryKey: ["interpreters"], queryFn: async () => (await api.get("/interpreters")).data });
  const { data: gmailStatus, refetch: refetchGmail } = useQuery({ queryKey: ["gmail-status"], queryFn: async () => (await api.get("/gmail/status")).data });

  const connectGmail = async () => {
    const r = await api.get("/gmail/oauth/start");
    window.location.href = r.data.auth_url;
  };

  const gmailSync = useMutation({
    mutationFn: async () => (await api.post("/gmail/sync")).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["comms"] }); toast.success(`Synced — ${d.inserted} new email${d.inserted === 1 ? "" : "s"} (${d.checked} checked)`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Sync failed"),
  });

  const disconnectGmail = async () => {
    await api.post("/gmail/disconnect");
    refetchGmail();
    toast.success("Gmail disconnected");
  };

  const create = useMutation({
    mutationFn: async (payload) => {
      const shared = {
        client_id: payload.client_id || null,
        project_id: payload.project_id || null,
        interpreter_id: payload.interpreter_id || null,
      };
      if (payload.send_now && payload.kind === "email" && payload.to) {
        // Real send via Resend — this also logs into communications collection server-side.
        return (await api.post("/emails/send", {
          to: payload.to,
          subject: payload.subject || "(no subject)",
          text: payload.body,
          ...shared,
        })).data;
      }
      return (await api.post("/communications", {
        kind: payload.kind,
        subject: payload.subject,
        body: payload.body,
        direction: payload.direction,
        ...shared,
      })).data;
    },
    onSuccess: (_, payload) => {
      qc.invalidateQueries({ queryKey: ["comms"] });
      toast.success(payload.send_now && payload.kind === "email" ? "Email sent" : "Logged");
      setOpen(false); setForm(empty);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const filtered = useMemo(() => filter === "all" ? comms : comms.filter(c => c.kind === filter), [comms, filter]);

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/communications/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["comms"] }); toast.success("Communication deleted"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const draft = async () => {
    setDrafting(true);
    try {
      const r = await api.post("/ai/draft-email", { prompt: form.body || form.subject || "Follow up", context: form.subject });
      setForm(f => ({ ...f, body: r.data.text }));
      toast.success("Draft ready");
    } catch { toast.error("AI failed"); } finally { setDrafting(false); }
  };

  const summarize = async () => {
    setDrafting(true);
    try {
      const r = await api.post("/ai/summarize-notes", { prompt: form.body });
      setForm(f => ({ ...f, body: r.data.text }));
      toast.success("Summarized");
    } catch { toast.error("AI failed"); } finally { setDrafting(false); }
  };

  const iconFor = (k) => k === "email" ? <EnvelopeSimple size={14} weight="duotone" /> : k === "call" ? <Phone size={14} weight="duotone" /> : <NotePencil size={14} weight="duotone" />;

  return (
    <div data-testid="comms-page">
      <PageHeader
        eyebrow="Communication Hub"
        title="Emails · Calls · Notes"
        description="Log every touchpoint with clients and interpreters. Draft or summarize with AI."
        actions={
          <div className="flex items-center gap-2">
            {gmailStatus?.connected ? (
              <>
                <div className="text-xs text-slate-600">
                  <GoogleLogo size={12} weight="fill" className="inline mr-1 text-blue-800" />
                  <span className="font-mono">{gmailStatus.gmail_email}</span>
                </div>
                <Button size="sm" variant="outline" onClick={() => gmailSync.mutate()} disabled={gmailSync.isPending} data-testid="gmail-sync-btn">
                  <ArrowsClockwise size={14} className={`mr-1 ${gmailSync.isPending ? "animate-spin" : ""}`} /> {gmailSync.isPending ? "Syncing…" : "Sync inbox"}
                </Button>
                <Button size="sm" variant="ghost" onClick={disconnectGmail} className="text-xs text-slate-500">Disconnect</Button>
              </>
            ) : (
              <Button size="sm" variant="outline" onClick={connectGmail} data-testid="gmail-connect-btn">
                <GoogleLogo size={14} weight="fill" className="mr-1.5 text-red-600" /> Connect Gmail
              </Button>
            )}
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-36" data-testid="comm-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="email">Emails</SelectItem>
                <SelectItem value="call">Calls</SelectItem>
                <SelectItem value="note">Notes</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setOpen(true)} data-testid="log-comm-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
              <Plus size={16} weight="bold" className="mr-1.5" /> Log
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <div className="card-flat p-10 text-center text-slate-400">Loading…</div>
      ) : filtered.length === 0 ? (
        <Empty title="Nothing logged yet" hint="Log your first email, call, or note." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="comms-list">
          {filtered.map((c) => (
            <button
              type="button"
              key={c.comm_id}
              onClick={() => setViewing(c)}
              className="card-flat p-5 hover-lift group relative text-left w-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid={`comm-card-${c.comm_id}`}
            >
              <button
                type="button"
                data-testid={`delete-comm-${c.comm_id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Delete this ${c.kind}? This cannot be undone.`)) del.mutate(c.comm_id);
                }}
                className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-600 p-1 rounded"
                title="Delete"
              >
                <Trash size={14} />
              </button>
              <div className="flex items-center justify-between mb-2 pr-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-blue-800">
                  {iconFor(c.kind)}{c.kind} · {c.direction}
                </div>
                <div className="text-xs text-slate-400">{fmtDate(c.created_at)}</div>
              </div>
              {c.subject && <div className="font-display font-semibold text-slate-900 mb-1">{c.subject}</div>}
              <div className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-6">{c.body}</div>
              <div className="flex items-center justify-between mt-3 text-[11px] text-slate-400">
                <span>by {c.author_email || "—"}{c.to ? ` · to ${c.to}` : ""}</span>
                {c.body_html && <span className="text-blue-600 group-hover:underline">View email →</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Communication detail sheet */}
      <Sheet open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <SheetContent className="w-full sm:max-w-2xl bg-white overflow-y-auto" data-testid="comm-detail-sheet">
          {viewing && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  {iconFor(viewing.kind)}
                  <span>{viewing.subject || `(no subject)`}</span>
                </SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3 text-xs text-slate-600 border border-slate-200 rounded-md p-3 bg-slate-50">
                  <div><span className="uppercase tracking-wider text-slate-400 text-[10px] block">From</span>{viewing.author_email || "system"}</div>
                  <div><span className="uppercase tracking-wider text-slate-400 text-[10px] block">To</span>{viewing.to || "—"}</div>
                  <div><span className="uppercase tracking-wider text-slate-400 text-[10px] block">Sent</span>{fmtDate(viewing.created_at)}</div>
                  <div><span className="uppercase tracking-wider text-slate-400 text-[10px] block">Provider</span>{viewing.provider || "—"} {viewing.provider_id ? `· ${viewing.provider_id.slice(0, 12)}…` : ""}</div>
                </div>

                {viewing.body_html ? (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Email preview</div>
                    <iframe
                      title="Email body"
                      srcDoc={viewing.body_html}
                      className="w-full h-[520px] border border-slate-200 rounded-md bg-white"
                      sandbox=""
                      data-testid="comm-detail-iframe"
                    />
                  </div>
                ) : (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Body</div>
                    <div className="border border-slate-200 rounded-md p-4 bg-white whitespace-pre-wrap text-sm text-slate-700" data-testid="comm-detail-body">
                      {viewing.body || "(empty)"}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
                  <div className="text-[11px] text-slate-500">
                    {viewing.contract_id ? `Linked contract: ${viewing.contract_id.slice(0, 18)}…` : ""}
                  </div>
                  <div className="flex items-center gap-2">
                    {viewing.body_html && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(viewing.body_html);
                          toast.success("HTML copied");
                        }}
                        data-testid="comm-copy-html-btn"
                      >
                        <Copy size={14} className="mr-1.5" /> Copy HTML
                      </Button>
                    )}
                    {viewing.kind === "email" && viewing.direction === "outbound" && viewing.to && (
                      <Button
                        size="sm"
                        onClick={() => resend.mutate(viewing.comm_id)}
                        disabled={resend.isPending}
                        className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
                        data-testid="comm-resend-btn"
                      >
                        <PaperPlaneRight size={14} className="mr-1.5" />
                        {resend.isPending ? "Sending…" : `Resend to ${viewing.to}`}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-xl bg-white overflow-y-auto">
          <SheetHeader><SheetTitle>Log communication</SheetTitle></SheetHeader>
          <div className="mt-6 space-y-4" data-testid="comm-form">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Kind">
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="comm-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="note">Note</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Direction">
                <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outbound">Outbound</SelectItem>
                    <SelectItem value="inbound">Inbound</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Client">
                <Select value={form.client_id || "__none__"} onValueChange={(v) => setForm({ ...form, client_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Project">
                <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Interpreter">
                <Select value={form.interpreter_id || "__none__"} onValueChange={(v) => setForm({ ...form, interpreter_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {interpreters.map(i => <SelectItem key={i.interpreter_id} value={i.interpreter_id}>{i.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Subject"><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="comm-subject" /></Field>
            <Field label="Body / notes">
              <Textarea rows={7} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="comm-body" />
            </Field>

            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={draft} disabled={drafting} data-testid="ai-draft-btn">
                <Sparkle size={14} weight="fill" className="text-blue-800 mr-1" /> AI draft email
              </Button>
              <Button size="sm" variant="outline" onClick={summarize} disabled={drafting} data-testid="ai-summarize-btn">
                <Sparkle size={14} weight="fill" className="text-blue-800 mr-1" /> Summarize
              </Button>
            </div>

            {form.kind === "email" && (
              <div className="border border-dashed border-blue-200 rounded-md p-3 bg-blue-50/40" data-testid="comm-send-block">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wider font-semibold text-blue-800 flex items-center gap-1.5">
                    <PaperPlaneTilt size={14} weight="fill" /> Send via Resend
                  </div>
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input type="checkbox" checked={form.send_now} onChange={(e) => setForm({ ...form, send_now: e.target.checked })} data-testid="comm-send-toggle" />
                    Send now (not just log)
                  </label>
                </div>
                {form.send_now && (
                  <div className="mt-3">
                    <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Recipient email</Label>
                    <Input className="mt-1" placeholder="client@example.com" type="email" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} data-testid="comm-to" />
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={() => create.mutate(form)} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="comm-save">
                {form.send_now && form.kind === "email" ? "Send email" : "Log entry"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
